import { useApiQuery } from './useApiQuery';
import type { IssueType } from '@overlayqa/shared';
import { useApiMutation } from './useApiMutation';
import { ticketsApi } from '../lib/api';
import type { TicketSeverity, TicketStatus } from '@overlayqa/shared';
import { log } from '@/lib/logger';
import { ISSUE_MUTATION_INVALIDATE_KEYS } from './issueMutationInvalidation';

log.info('[useIssues] Module loaded');

interface IssueFilters {
  projectId?: string;
  status?: string;
  severity?: string;
  type?: string;
}

export function useIssues(filters?: IssueFilters, options?: { refetchInterval?: number | false }) {
  log.info('[useIssues] useIssues called with filters:', { filters });
  return useApiQuery({
    queryKey: ['issues', filters],
    queryFn: (token) => ticketsApi.list(token, filters),
    placeholderPreviousData: true,
    // Board status stays current even when another tab/surface made the edit.
    refetchInterval: options?.refetchInterval,
  });
}

export function useIssue(id: string) {
  log.info('[useIssues] useIssue called with id:', { id });
  return useApiQuery({
    queryKey: ['issues', id],
    queryFn: (token) => ticketsApi.get(token, id),
    enabled: !!id,
  });
}

interface CreateIssueData {
  projectId: string;
  createdById: string;
  title: string;
  description?: string;
  severity: TicketSeverity;
  type: IssueType;
  screenshotUrl: string;
  pageUrl: string;
  viewportWidth?: number;
  viewportHeight?: number;
  devicePixelRatio?: number;
  figmaFileKey?: string;
  figmaNodeId?: string;
}

export function useCreateIssue() {
  log.info('[useIssues] useCreateIssue called');
  return useApiMutation<unknown, CreateIssueData>({
    mutationFn: (token, data) => ticketsApi.create(token, data),
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
  });
}

interface UpdateIssueData {
  title?: string;
  description?: string;
  severity?: TicketSeverity;
  type?: string;
  status?: TicketStatus;
  assigneeId?: string | null;
  browserName?: string;
  browserVersion?: string;
  os?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  screenWidth?: number;
  screenHeight?: number;
  devicePixelRatio?: number;
  // Fix verification: failed-verification reopen sets regressed=true.
  regressed?: boolean;
  /** Gesture tag for issue_status_changed; the server never stores it. */
  via?: 'not_detected_chip';
}

export function useUpdateIssue() {
  log.info('[useIssues] useUpdateIssue called');
  return useApiMutation<unknown, { id: string; data: UpdateIssueData }>({
    mutationFn: (token, { id, data }) => ticketsApi.update(token, id, data),
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
    onSuccess: (_data, variables) => {
      log.info('[useIssues] useUpdateIssue onSuccess for issue:', { id: variables.id });
    },
  });
}

export function useDeleteIssue() {
  log.info('[useIssues] useDeleteIssue called');
  return useApiMutation<unknown, string>({
    mutationFn: (token, id) => ticketsApi.delete(token, id),
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
  });
}

